// Résolution de la réponse d'import PDF, partagée par tous les appelants.
//
// Le serveur ne peut pas toujours renvoyer sa réponse directement : Cloud Run
// coupe à 32 Mio EN SORTIE aussi, et un fond de page A4 rendu à 300 DPI dépasse
// déjà ce budget une fois encodé en base64. Au-delà, le serveur dépose le JSON
// complet sur S3 et ne renvoie qu'une URL (`payload_url`).
//
// Tout appelant de /api/pdf/import doit donc passer par ici, sinon le document
// arrive muet — ni image de fond, ni calques — sans le moindre message d'erreur.
export async function resolvePdfPayload(data, { signal, timeoutMs = 180000 } = {}) {
    if (!data || !data.payload_url) return data;

    // Délai maximal indispensable : un stockage qui accepte la connexion puis ne
    // répond plus (réseau d'entreprise, portail captif) laisserait la promesse en
    // suspens pour toujours, et l'interface bloquée sur son écran d'attente.
    const ctrl = new AbortController();
    const relayAbort = () => ctrl.abort();
    signal?.addEventListener('abort', relayAbort);
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
        const res = await fetch(data.payload_url, { signal: ctrl.signal });
        if (!res.ok) {
            throw new Error(`Le résultat de l'import n'a pas pu être récupéré depuis le stockage (HTTP ${res.status}).`);
        }
        return await res.json();
    } catch (err) {
        if (err.name === 'AbortError') {
            const aborted = !!signal?.aborted;
            const e = new Error(aborted
                ? 'Import annulé.'
                : "Le résultat de l'import n'a pas pu être récupéré : délai dépassé.");
            e.code = aborted ? 'ABORTED' : 'PAYLOAD_TIMEOUT';
            throw e;
        }
        throw err;
    } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', relayAbort);
    }
}

/**
 * L'image de la PREMIÈRE PAGE d'un PDF, en URL data: — pour joindre une étiquette modèle à une
 * demande à l'agent (utils/pieceJointeAgent.js). Passe par la voie « Aplati » de l'import
 * (`mode=template`, rendu 300 DPI géré ICC du backend) : aucune dépendance de rendu PDF dans
 * le navigateur, le moteur reste celui que l'import emploie déjà. Voie directe seulement
 * (30 Mo côté serveur ; l'onglet Agent borne à 25).
 */
export async function premierePagePdfEnImage(fichier, { signal } = {}) {
    const [{ useAuthStore }, { API_URL }] = await Promise.all([import('../store/useAuthStore'), import('./client')]);
    const fd = new FormData();
    fd.append('mode', 'template');
    fd.append('file', fichier);
    const res = await useAuthStore.getState().authFetch(`${API_URL}/api/pdf/import`, { method: 'POST', body: fd, signal });
    if (!res.ok) {
        let detail = `le serveur a répondu ${res.status}`;
        try { const j = await res.json(); if (typeof j.detail === 'string') detail = j.detail; } catch { /* pas du JSON */ }
        throw new Error(`« ${fichier.name} » : conversion du PDF impossible (${detail}).`);
    }
    const data = await resolvePdfPayload(await res.json(), { signal });
    if (!data?.image) throw new Error(`« ${fichier.name} » : le PDF n'a pas donné d'image.`);
    return data.image;
}
